// ============================================================================
// ScannerBollaFoto.jsx
// Componente riusabile: apre fotocamera, ritaglia, genera PDF come File object.
//
// A differenza di ScansionaBolla.jsx (flusso completo operaio), questo NON
// salva su Firestore ne' chiama l'AI: si limita a produrre un File PDF
// e lo passa al callback, cosi' chi lo usa puo' poi chiamare qualunque
// funzione esistente (es. importaBollaDaPDF del titolare).
//
// DIPENDENZE: jspdf
//
// PROPS:
//   onPDFPronto : function(file: File) -> chiamato col PDF generato
//   onChiudi    : function()           -> chiude lo scanner
// ============================================================================

import React, { useState, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';

// Tema coerente con Gestionale
const theme = {
  bg: "#0f1117",
  surface: "#1a1d27",
  border: "#2a2e45",
  accent: "#f5a623",
  text: "#e8eaf2",
  textMuted: "#7b82a0",
  textDim: "#4a5068",
};

const btnPrimarioSt = {
  background: theme.accent, color: "#1a1d27", fontWeight: 700, fontSize: 16,
  border: "none", borderRadius: 10, padding: "14px 20px", cursor: "pointer",
  fontFamily: "'DM Sans',sans-serif",
};

const btnSecondarioSt = {
  background: "transparent", color: theme.text, fontWeight: 500, fontSize: 15,
  border: `1px solid ${theme.border}`, borderRadius: 10, padding: "12px 18px",
  cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
};

export default function ScannerBollaFoto({ onPDFPronto, onChiudi }) {
  const [fotoOriginale, setFotoOriginale] = useState(null);
  const [elaborando, setElaborando] = useState(false);
  const inputFileRef = useRef(null);

  const handleFotoScattata = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setFotoOriginale(ev.target.result);
    reader.readAsDataURL(file);
    if (inputFileRef.current) inputFileRef.current.value = "";
  };

  const handleConferma = async (dataURLRitagliata) => {
    setElaborando(true);
    try {
      const pdfBlob = await immagineToPDFBlob(dataURLRitagliata);
      const file = new File([pdfBlob], `bolla_${Date.now()}.pdf`, { type: 'application/pdf' });
      onPDFPronto(file);
    } catch (err) {
      console.error(err);
      alert("Errore nella generazione del PDF: " + err.message);
      setElaborando(false);
    }
  };

  // Modal overlay fullscreen
  const overlaySt = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: theme.bg, zIndex: 9999, overflow: 'auto', padding: 16,
  };

  // Modo ritaglio
  if (fotoOriginale && !elaborando) {
    return (
      <div style={overlaySt}>
        <Ritaglio
          immagine={fotoOriginale}
          onAnnulla={() => setFotoOriginale(null)}
          onConferma={handleConferma}
        />
      </div>
    );
  }

  // Modo elaborazione PDF
  if (elaborando) {
    return (
      <div style={overlaySt}>
        <div style={{ maxWidth: 520, margin: "80px auto 0", textAlign: "center" }}>
          <div style={spinnerSt} />
          <div style={{ marginTop: 20, color: theme.textMuted, fontSize: 15 }}>
            Preparazione PDF...
          </div>
        </div>
      </div>
    );
  }

  // Modo iniziale: apri fotocamera
  return (
    <div style={overlaySt}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 22, color: theme.accent }}>
            📷 Scannerizza bolla
          </div>
          <button onClick={onChiudi} style={{ ...btnSecondarioSt, padding: "8px 14px" }}>✕</button>
        </div>

        <div style={{ fontSize: 14, color: theme.textMuted, marginBottom: 24, lineHeight: 1.5 }}>
          Posiziona la bolla su una superficie piana e illumina bene.<br />
          Dopo lo scatto potrai ritagliare il foglio.
        </div>

        <input
          ref={inputFileRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFotoScattata}
          style={{ display: 'none' }}
        />

        <button
          onClick={() => inputFileRef.current?.click()}
          style={{ ...btnPrimarioSt, width: "100%" }}>
          📷 Apri fotocamera
        </button>

      </div>
    </div>
  );
}

// ============================================================================
// Componente Ritaglio (4 angoli trascinabili, identico a ScansionaBolla)
// ============================================================================
function Ritaglio({ immagine, onAnnulla, onConferma }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [angoli, setAngoli] = useState(null);
  const [trascinaIdx, setTrascinaIdx] = useState(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!immagine) return;
    const img = new Image();
    img.onload = () => {
      const containerW = Math.min(containerRef.current?.clientWidth || 360, 480);
      const ratio = img.height / img.width;
      const cw = containerW;
      const ch = containerW * ratio;
      setImgSize({ w: img.width, h: img.height });
      setCanvasSize({ w: cw, h: ch });
      const margin = 20;
      setAngoli([
        { x: margin, y: margin },
        { x: cw - margin, y: margin },
        { x: cw - margin, y: ch - margin },
        { x: margin, y: ch - margin },
      ]);
    };
    img.src = immagine;
  }, [immagine]);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv || !angoli || !immagine) return;
    cv.width = canvasSize.w;
    cv.height = canvasSize.h;
    const ctx = cv.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvasSize.w, canvasSize.h);
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      ctx.fillRect(0, 0, canvasSize.w, canvasSize.h);
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.moveTo(angoli[0].x, angoli[0].y);
      for (let i = 1; i < 4; i++) ctx.lineTo(angoli[i].x, angoli[i].y);
      ctx.closePath();
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = theme.accent;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(angoli[0].x, angoli[0].y);
      for (let i = 1; i < 4; i++) ctx.lineTo(angoli[i].x, angoli[i].y);
      ctx.closePath();
      ctx.stroke();
      angoli.forEach(a => {
        ctx.fillStyle = theme.surface;
        ctx.strokeStyle = theme.accent;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(a.x, a.y, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      });
    };
    img.src = immagine;
  }, [angoli, canvasSize, immagine]);

  const getEventPos = e => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0];
    const cx = t ? t.clientX : e.clientX;
    const cy = t ? t.clientY : e.clientY;
    return { x: cx - rect.left, y: cy - rect.top };
  };
  const handleStart = e => {
    e.preventDefault();
    const pos = getEventPos(e);
    let bestIdx = -1, bestDist = 35;
    angoli.forEach((a, i) => {
      const d = Math.hypot(a.x - pos.x, a.y - pos.y);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    });
    if (bestIdx >= 0) setTrascinaIdx(bestIdx);
  };
  const handleMove = e => {
    if (trascinaIdx === null) return;
    e.preventDefault();
    const pos = getEventPos(e);
    const x = Math.max(0, Math.min(canvasSize.w, pos.x));
    const y = Math.max(0, Math.min(canvasSize.h, pos.y));
    setAngoli(angoli.map((a, i) => i === trascinaIdx ? { x, y } : a));
  };
  const handleEnd = () => setTrascinaIdx(null);

  const conferma = () => {
    if (!angoli) return;
    const sx = imgSize.w / canvasSize.w;
    const sy = imgSize.h / canvasSize.h;
    const reali = angoli.map(a => ({ x: a.x * sx, y: a.y * sy }));
    const minX = Math.min(...reali.map(a => a.x));
    const maxX = Math.max(...reali.map(a => a.x));
    const minY = Math.min(...reali.map(a => a.y));
    const maxY = Math.max(...reali.map(a => a.y));
    const w = maxX - minX, h = maxY - minY;
    const img = new Image();
    img.onload = () => {
      const out = document.createElement('canvas');
      out.width = w;
      out.height = h;
      const ctx = out.getContext('2d');
      ctx.drawImage(img, minX, minY, w, h, 0, 0, w, h);
      onConferma(out.toDataURL('image/jpeg', 0.85));
    };
    img.src = immagine;
  };

  return (
    <div ref={containerRef} style={{ maxWidth: 520, margin: "0 auto" }}>
      <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 22, color: theme.accent, marginBottom: 8 }}>
        ✂️ Ritaglia bolla
      </div>
      <div style={{ fontSize: 14, color: theme.textMuted, marginBottom: 20 }}>
        Trascina i 4 angoli per selezionare il foglio
      </div>
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <canvas
          ref={canvasRef}
          style={{ touchAction: "none", maxWidth: "100%", borderRadius: 8, display: "block", margin: "0 auto" }}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onAnnulla} style={{ ...btnSecondarioSt, flex: 1 }}>Rifai</button>
        <button onClick={conferma} style={{ ...btnPrimarioSt, flex: 2, width: "auto" }}>Conferma</button>
      </div>
    </div>
  );
}

// ============================================================================
// Utility: immagine -> PDF Blob (per creare File object)
// ============================================================================
async function immagineToPDFBlob(dataURL) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
        const pageW = 210, pageH = 297;
        const ratio = img.height / img.width;
        let w = pageW - 20, h = w * ratio;
        if (h > pageH - 20) { h = pageH - 20; w = h / ratio; }
        pdf.addImage(dataURL, 'JPEG', (pageW - w) / 2, (pageH - h) / 2, w, h);
        const blob = pdf.output('blob');
        resolve(blob);
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error('Caricamento immagine fallito'));
    img.src = dataURL;
  });
}

const spinnerSt = {
  width: 44, height: 44, margin: "0 auto",
  border: `4px solid ${theme.border}`, borderTop: `4px solid ${theme.accent}`,
  borderRadius: "50%", animation: "spin 1s linear infinite",
};

if (typeof document !== 'undefined' && !document.getElementById('scanner-bolla-foto-spin')) {
  const s = document.createElement('style');
  s.id = 'scanner-bolla-foto-spin';
  s.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(s);
}
